import {findEntryPointQuery} from 'src/internal/revamp/graphql/documentUtils/entrypoint';

import type {
  DefinitionNode,
  DocumentNode,
  FragmentDefinitionNode,
  SelectionNode,
} from 'graphql';
import type {FieldNameData} from 'src/internal/revamp/queryUtils/types';

/**
 * Extracts top-level field aliases and names from a graphql operation.
 * @param {DocumentNode} document - The GraphQL document
 * @returns {FieldNameData[]} - Array of top-level field aliases and names
 */

export function extractTopLevelFieldNameData(
  document: DocumentNode,
): FieldNameData[] {
  const topLevelFields: FieldNameData[] = [];
  // [1] Find the document definition
  const queryDefinition = findEntryPointQuery(document);

  // [2] Extract aliases and field names from the selections
  queryDefinition.selectionSet.selections.forEach(
    (selection: SelectionNode) => {
      // [2.1] Handle top level fields
      if (selection.kind === 'Field') {
        topLevelFields.push({
          alias: selection.alias?.value,
          name: selection.name.value,
        });
      }
      // [2.2] Handle named fragments
      else if (selection.kind === 'FragmentSpread') {
        // Find the fragment definition
        const fragmentDef = document.definitions.find(
          (def: DefinitionNode): def is FragmentDefinitionNode =>
            def.kind === 'FragmentDefinition' &&
            'name' in def &&
            def.name.value === selection.name.value,
        );

        if (
          fragmentDef &&
          fragmentDef.selectionSet &&
          fragmentDef.selectionSet.selections
        ) {
          fragmentDef.selectionSet.selections.forEach(
            (fragmentSelection: SelectionNode) => {
              if (fragmentSelection.kind === 'Field') {
                topLevelFields.push({
                  alias: fragmentSelection.alias?.value,
                  name: fragmentSelection.name.value,
                });
              }
            },
          );
        }
      }
      // [2.3] Handle inline fragments
      else if (selection.kind === 'InlineFragment' && selection.selectionSet) {
        selection.selectionSet.selections.forEach(
          (fragmentSelection: SelectionNode) => {
            if (fragmentSelection.kind === 'Field') {
              topLevelFields.push({
                alias: fragmentSelection.alias?.value,
                name: fragmentSelection.name.value,
              });
            }
          },
        );
      }
    },
  );

  return topLevelFields;
}
